import { Injectable, inject } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { filter, map } from "rxjs/operators";
import { NavigationActions } from "./navigation.actions";
import { parseNavigationUrl } from "./navigation.util";
import { SidebarGroup } from "./navigation.state";

@Injectable()
export class NavigationEffects {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly router = inject(Router);
    private readonly actions$ = inject(Actions);

    // =========================================================================
    // EFFECTS
    // =========================================================================

    /**
     * Syncs navigation state from URL changes.
     * Listens to router navigation events and dispatches setView action.
     */
    syncFromRouter$ = createEffect(() =>
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(event => ({ url: event.urlAfterRedirects, ...parseNavigationUrl(event.urlAfterRedirects) })),
            map(({ view, activeTab, url }) => NavigationActions.setView({ view, activeTab, url })),
        ),
    );

    /**
     * Auto-expand sidebar group when navigating to a page within that group.
     */
    autoExpandSidebarGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NavigationActions.setView),
            map(({ url }) => this.getSidebarGroupForUrl(url)),
            filter((group): group is SidebarGroup => group !== null),
            map(group => NavigationActions.expandSidebarGroup({ group })),
        ),
    );

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================

    private getSidebarGroupForUrl(url: string): SidebarGroup | null {
        if (url.includes("/central-support")) return "central-support";
        if (url.includes("/contract-admin")) return "contract-admin";
        if (url.includes("/super-user") || url.includes("/exchange-rates")) return "super-user";
        if (url.includes("/developer") || url.includes("/sales-order")) return "developer";
        return null;
    }
}
